import React, { useState, useEffect } from 'react';
import {
  Card,
  Col,
  Row,
  Typography,
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Badge,
} from 'antd';

const { RangePicker } = DatePicker;
import { LoadingOutlined } from '@ant-design/icons';
import { useProjectContext } from '../../../../core/components/projects/useProjectContext';
import { withRouter } from 'react-router-dom';
import { debounce } from 'lodash';

import { PageContentLayout, PageHeaderLayout } from '../../../shared/layout';
import { useUserContext } from '../../../../core/components/users/useUserContext';
import { startCase } from 'lodash';
import { projectStatusOptions, projectPriorityOptions } from '../configs/project.config';
import { useWindowWidth } from '../../../../core/hooks/useWindowWidth';
import '../styles/SelectInput.css';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const ProjectForm = ({ history }) => {
  const windowWidth = useWindowWidth();
  const [form] = Form.useForm();
  const { currentUser, createProject } = useProjectContext();
  const { fetchBasicUsersDetails, basicUsersDetails, basicUsersDetailsLoading } = useUserContext();

  const [teamUsers, setTeamUsers] = useState([]);
  const [ownerUsers, setOwnerUsers] = useState([]);
  const [buttonDisabled, setButtonDisabled] = useState(false);

  const level =
    currentUser && currentUser.role && currentUser.role.org && currentUser.role.org.level;

  const fetchOwnerUsers = debounce((name = '') => {
    const currentUserOrgId = currentUser?.role?.org?.id;
    if (!currentUserOrgId) {
      return;
    }
    fetchBasicUsersDetails({
      variables: {
        first: 100,
        skip: 0,
        where: {
          AND: [
            {
              roles_some: {
                org: {
                  id: currentUserOrgId,
                },
                roleItems_some: {
                  feature: 'TASK_MANAGEMENT',
                },
              },
              name_contains: name,
            },
          ],
        },
      },
    });
  }, 500);

  const fetchTeamUsers = debounce((name = '') => {
    const currentUserOrgId = currentUser?.role?.org?.id;
    if (!currentUserOrgId) {
      setTeamUsers([]);
      return;
    }
    fetchBasicUsersDetails({
      variables: {
        first: 100,
        skip: 0,
        where: {
          AND: [
            {
              roles_some: {
                org: {
                  id: currentUserOrgId,
                },
                roleItems_some: {
                  feature: 'TASK_MANAGEMENT',
                },
              },
              name_contains: name,
            },
          ],
        },
      },
    });
  }, 500);

  useEffect(() => {
    if (basicUsersDetails && basicUsersDetails.length > 0) {
      setTeamUsers(basicUsersDetails);
      setOwnerUsers(basicUsersDetails);
    }
  }, [basicUsersDetails]);

  const handleSubmit = async () => {
    const {
      name,
      description,
      dateRange,
      status,
      priority,
      ownerId,
      teamMembers,
    } = form.getFieldsValue();

    await form.validateFields();

    const owner = ownerId
      ? {
          connect: {
            id: ownerId,
          },
        }
      : {
          connect: {
            id: currentUser.id,
          },
        };

    const team = teamMembers && teamMembers.length > 0
      ? {
          connect: teamMembers.map((userId) => ({ id: userId })),
        }
      : undefined;

    const data = {
      name,
      description,
      owner,
      team,
      startDate: dateRange && dateRange[0] ? dateRange[0].toISOString() : undefined,
      endDate: dateRange && dateRange[1] ? dateRange[1].toISOString() : undefined,
      status: status || 'NOT_STARTED',
      priority,
    };

    try {
      message.loading('Creating project...', 0);
      await createProject({ variables: { data } });
      message.destroy();
      message.success('Project created successfully');
      history.push(`/tasks/projects`);
    } catch (error) {
      message.destroy();
      message.error(`Failed to create project: ${error.message}`);
      console.error('Project creation error:', error);
    }
  };

  return (
    <>
      <PageHeaderLayout
        title="Create Project"
        style={{
          fontSize: windowWidth < 768 ? '18px' : '22px',
          display: 'flex',
          border: 'none',
          paddingInline: windowWidth < 768 ? '4%' : '10%',
          paddingTop: '2vh',
        }}
      />
      <PageContentLayout
        style={{
          backgroundColor: 'white',
          display: 'flex',
          justifyContent: 'center',
          paddingInline: windowWidth < 768 ? '4%' : '10%',
          width: '100%',
          height: '100%',
        }}
      >
        <Form form={form} layout="vertical" style={{ width: '100%', height: '100%' }}>
          <Row>
            <Col style={{ width: '100%', marginBlock: '3vh' }}>
              <Card
                style={{ borderRadius: '7px', backgroundColor: '#f9fafb' }}
                title={<Text style={{ fontSize: windowWidth < 768 ? '16px' : '18px', fontWeight: 'bold' }}>PROJECT DETAILS</Text>}
                headStyle={{ borderBottom: 'none' }}
              >
                <Row gutter={windowWidth < 768 ? [16, 16] : 32}>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      name="name"
                      label={
                        <div>
                          <div>Project Name</div>
                          <div style={{ color: 'gray', fontSize: '12px' }}>
                            The name you will identify this project by
                          </div>
                        </div>
                      }
                      rules={[{ required: true, message: 'Project Name is Required' }]}
                      colon={false}
                    >
                      <Input placeholder="Enter project name" className="custom-input" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      name="ownerId"
                      label={
                        <div>
                          <div>Owner</div>
                          <div style={{ color: 'gray', fontSize: '12px' }}>
                            The person responsible for this project
                          </div>
                        </div>
                      }
                      colon={false}
                    >
                      <Select
                        placeholder="Select owner"
                        showSearch
                        loading={basicUsersDetailsLoading}
                        onFocus={() => fetchOwnerUsers()}
                        onSearch={(searchText) => fetchOwnerUsers(searchText)}
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        className="custom-select"
                      >
                        {ownerUsers.map((user) => (
                          <Option key={user.id} value={user.id}>
                            {startCase(user.name || user.username)}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={windowWidth < 768 ? [16, 16] : 32}>
                  <Col span={24}>
                    <Form.Item
                      name="description"
                      label={
                        <div>
                          <div>Description</div>
                          <div style={{ color: 'gray', fontSize: '12px' }}>
                            Optional: Describe the project goals and objectives
                          </div>
                        </div>
                      }
                      colon={false}
                    >
                      <TextArea
                        placeholder="Enter project description"
                        rows={4}
                        maxLength={3000}
                        className="custom-textarea"
                        style={{ minHeight: '100px' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={windowWidth < 768 ? [16, 16] : 32}>
                  <Col span={24}>
                    <Form.Item
                      name="dateRange"
                      label={
                        <div>
                          <div>Timeline</div>
                          <div style={{ color: 'gray', fontSize: '12px' }}>
                            Optional: Project start and end dates
                          </div>
                        </div>
                      }
                      colon={false}
                    >
                      <RangePicker
                        style={{ width: '100%', minWidth: windowWidth < 768 ? '100%' : '300px' }}
                        className="custom-input"
                        format="YYYY-MM-DD"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={windowWidth < 768 ? [16, 16] : 32}>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      name="status"
                      label={
                        <div>
                          <div>Status</div>
                          <div style={{ color: 'gray', fontSize: '12px' }}>
                            Current status of the project
                          </div>
                        </div>
                      }
                      colon={false}
                      initialValue="NOT_STARTED"
                    >
                      <Select placeholder="Select status" className="custom-select">
                        {projectStatusOptions.map((option) => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      name="priority"
                      label={
                        <div>
                          <div>Priority</div>
                          <div style={{ color: 'gray', fontSize: '12px' }}>
                            Optional: Set the priority level of this project
                          </div>
                        </div>
                      }
                      colon={false}
                    >
                      <Select placeholder="Select priority" className="custom-select">
                        {projectPriorityOptions.map((option) => (
                          <Option key={option.value} value={option.value}>
                            <Badge color={option.color} style={{ marginRight: 8 }} />
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={windowWidth < 768 ? [16, 16] : 32}>
                  <Col span={24}>
                    <Form.Item
                      name="teamMembers"
                      label={
                        <div>
                          <div>Team Members</div>
                          <div style={{ color: 'gray', fontSize: '12px' }}>
                            Optional: Select team members for this project
                          </div>
                        </div>
                      }
                      colon={false}
                    >
                      <Select
                        mode="multiple"
                        placeholder="Select team members"
                        showSearch
                        loading={basicUsersDetailsLoading}
                        onFocus={() => fetchTeamUsers()}
                        onSearch={(searchText) => fetchTeamUsers(searchText)}
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        className="custom-select"
                      >
                        {teamUsers.map((user) => (
                          <Option key={user.id} value={user.id}>
                            {startCase(user.name || user.username)}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col
              span={24}
              style={{
                display: 'flex',
                flexDirection: windowWidth < 768 ? 'column' : 'row',
                justifyContent: windowWidth < 768 ? 'stretch' : 'right',
                gap: windowWidth < 768 ? '12px' : '2px',
                marginTop: '20px',
                paddingBottom: '15px',
              }}
            >
              {level === 'PLATFORM' && (
                <Button
                  onClick={() => history.goBack()}
                  style={{
                    fontSize: '15px',
                    height: '41px',
                    borderRadius: '5px',
                    width: windowWidth < 768 ? '100%' : 'auto',
                    marginRight: windowWidth < 768 ? '0' : '8px',
                  }}
                >
                  Back
                </Button>
              )}
              <Button
                onClick={async () => {
                  setButtonDisabled(true);
                  try {
                    await handleSubmit();
                  } catch (error) {
                    if (error.errorFields) {
                      const errorMessages = error.errorFields
                        .map((field) => field.errors.join(', '))
                        .join(', ');
                      message.destroy();
                      message.error(errorMessages);
                    } else {
                      message.error(error.message || 'An error occurred');
                    }
                  } finally {
                    setButtonDisabled(false);
                  }
                }}
                style={{
                  fontSize: '15px',
                  height: '41px',
                  borderRadius: '5px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  width: windowWidth < 768 ? '100%' : 'auto',
                }}
                disabled={buttonDisabled}
              >
                {buttonDisabled && <LoadingOutlined />} Create Project
              </Button>
            </Col>
          </Row>
        </Form>
      </PageContentLayout>
    </>
  );
};

export default withRouter(ProjectForm);